import React, { useCallback } from 'react'
import styles from './styles/search-results.module.scss'
import LoadMore from 'components/generic/LoadMore'
import i18n from 'constants/strings'
import {
  LG_TABLET_BREAKPOINT,
  PLAYERS_FETCH_SIZE_ANONYMOUS,
  PLAYERS_FETCH_SIZE_LOGGED_IN,
} from 'constants/global'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { Player } from 'types/global'
import useWindowSize from 'hooks/use-windowSize'
import SearchResultMobile from './SearchResultMobile'
import MainContainer from 'components/generic/MainContainer'
import NoDataContainer from 'components/generic/NoDataContainer'
import SearchResultsTableDesktop from './SearchResultsTableDesktop'
import usePlayerSearchWithSorting from 'hooks/use-player-search-with-sorting'
import SearchResultsCounter from './SearchResultsCounter'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import SearchResult from './SearchResultDesktop'
import WatchlistToggle from 'components/watchlist/WatchlistToggle'
import { selectOwnTennisId } from 'redux/selectors'

interface Props {
  searchId?: string
}

const SearchResults = ({ searchId = 'player-search' }: Props) => {
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const ownTennisId = useSelector(selectOwnTennisId)
  const windowWidth = useWindowSize().width
  const fetchSize = isLoggedIn ? PLAYERS_FETCH_SIZE_LOGGED_IN : PLAYERS_FETCH_SIZE_ANONYMOUS
  const {
    loadedPlayers,
    totalPlayers,
    isSearchPending,
    sorting,
    loadMorePlayers,
    allPlayersLoaded,
    isLoadingMorePlayers,
    handleTableSorting,
  } = usePlayerSearchWithSorting(searchId, { pageSize: fetchSize })

  const renderTableRow = useCallback(
    (p: Player) => {
      const showWatchlist = isLoggedIn && !!ownTennisId && p.tennisID !== ownTennisId
      return (
        <SearchResult key={p.id} player={p} withLink>
          <td className={styles.watchlistCell} aria-hidden={!showWatchlist}>
            {showWatchlist && <WatchlistToggle player={p} />}
          </td>
        </SearchResult>
      )
    },
    [isLoggedIn, ownTennisId]
  )

  return (
    <section className={styles.searchResultsSectionWrapper}>
      <MainContainer>
        <SearchResultsCounter
          loadedResults={loadedPlayers?.length}
          totalResults={totalPlayers}
          isLoading={!totalPlayers && isSearchPending}
          withDivider
        />
        {(isLoadingMorePlayers && !loadedPlayers?.length) ||
        (isSearchPending && !loadedPlayers?.length) ? (
          <LoadingSpinnerWithContainer
            containerHeight={500}
            spinnerWidth={90}
            classNames={{ spinner: styles.spinner }}
          />
        ) : loadedPlayers && loadedPlayers.length > 0 ? (
          windowWidth && windowWidth > LG_TABLET_BREAKPOINT ? (
            <div className={styles.searchResultsContent}>
              <SearchResultsTableDesktop
                players={loadedPlayers}
                sorting={sorting}
                handleSelectSorting={handleTableSorting}
                renderTableRow={renderTableRow}
                classHiddenCell={styles.watchlistCell}
              />
              {!allPlayersLoaded && (
                <div className={styles.btnWrapper}>
                  <LoadMore loading={isLoadingMorePlayers} loadMore={loadMorePlayers} />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.searchResultsContentMobile}>
              <ul className={styles.searchResultsListMobile}>
                {loadedPlayers.map(p => (
                  <li key={p.id}>
                    <SearchResultMobile
                      player={p}
                      withWatchlistBadge={isLoggedIn && p.tennisID !== ownTennisId}
                    />
                  </li>
                ))}
              </ul>
              {!allPlayersLoaded && (
                <div className={styles.btnWrapper}>
                  <LoadMore loading={isLoadingMorePlayers} loadMore={loadMorePlayers} />
                </div>
              )}
            </div>
          )
        ) : (
          <NoDataContainer className={styles.noData}>
            <p className={styles.noDataText}>{i18n.t('searchResults.noPlayers')}</p>
          </NoDataContainer>
        )}
      </MainContainer>
    </section>
  )
}

export default SearchResults
